import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faUser, faFileInvoice, faCalendarAlt, faHandshake, faMoneyBillWave, faFileAlt, faSearch, faBox, faTruck, faIdCard, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import { fetchHMSEntities } from '../../services/hmsEntityService';
import { crmService } from '../../services/crmService';
import { dashboardService } from '../../services/dashboardService';
import { useUserInfo } from '../../contexts/UserContext';
import { resolveIssuerName } from '../../utils/issuerDisplay';
import InvoiceDatePicker from '../../components/ui/InvoiceDatePicker';
import InvoiceValidationModal, { clearFieldError } from '../../components/Invoicing/InvoiceValidationModal';
import './OutgoingInvoice.css';

/**
 * Screen 1: Header & Partner Information for INCOMING INVOICES
 * 
 * Fields:
 * - HMS Entity (dropdown or from delivery)
 * - Issuer (automatic - logged in user)
 * - Supplier Invoice Number (from received invoice)
 * - HMS Expense Number (auto-generated AP-XXXXXXXX after save)
 * - Invoice Date (from invoice)
 * - Internal Reference (delivery number, contract numbers, purchase recap numbers)
 * - Partner Name (Supplier from CRM or Delivery, option to edit)
 * - Creditor Nr (from CRM Partner, read-only)
 * - Delivery Date (from delivery or manual)
 * - Due Date (from invoice)
 * - Payment Terms (from invoice)
 * - Supply Reference (text field)
 */

const IncomingInvoiceScreen1 = ({ 
  invoiceData, 
  setInvoiceData, 
  onNext, 
  sourceDelivery = null,
  sourceProforma = null,
  isReadOnly = false,
  captureSupplierName = null,
  capturePartner = null,
  issuerName = null,
  isPersisted = false
}) => {
  const [loading, setLoading] = useState(true);
  const [hmsEntities, setHmsEntities] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);

  // Validation modal state

  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationFieldIds, setValidationFieldIds] = useState([]);
  
  // Helper function to parse internal reference into separate parts
  const parseInternalReference = (internalRef) => {
    if (!internalRef) return { delivery: '', contracts: '', purchaseRecaps: '' };
    
    const lines = internalRef.split('\n').map(line => line.trim()).filter(line => line);
    let delivery = '';
    let contracts = [];
    let purchaseRecaps = '';
    
    lines.forEach(line => {
      if (line.startsWith('Delivery:')) {
        delivery = line.replace('Delivery:', '').trim();
      } else if (line.includes('Contract:')) {
        contracts.push(line.replace('Seller Contract:', '').replace('Buyer Contract:', '').trim());
      } else if (line.startsWith('Purchase Recaps:')) {
        purchaseRecaps = line.replace('Purchase Recaps:', '').trim();
      }
    });
    
    return {
      delivery,
      contracts: contracts.join(', '),
      purchaseRecaps
    };
  };
  
  // Update internal reference from separate fields
  const updateInternalReference = (field, value) => {
    const current = parseInternalReference(invoiceData.internal_reference);
    current[field] = value;
    
    const lines = [];
    if (current.delivery) lines.push(`Delivery: ${current.delivery}`);
    if (current.contracts) lines.push(`Contracts: ${current.contracts}`);
    if (current.purchaseRecaps) lines.push(`Purchase Recaps: ${current.purchaseRecaps}`);
    
    setInvoiceData(prev => ({ ...prev, internal_reference: lines.join('\n') }));
  };
  
  // Partner search with debouncing
  const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
  const [debouncedPartnerSearch, setDebouncedPartnerSearch] = useState('');
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
  const [partnerSearchLoading, setPartnerSearchLoading] = useState(false);
  const [selectedPartnerAddress, setSelectedPartnerAddress] = useState('');
  const partnerSearchRef = useRef(null);
  
  // Get current user info
  const { userInfo, updateUserInfo } = useUserInfo();
  const [sessionUserName, setSessionUserName] = useState('');
  
  useEffect(() => {
    loadDropdownData();
    loadIssuerInfo();
  }, []);

  // Auto-match capture hints once dropdowns are loaded AND capture data is available
  useEffect(() => {
    if (!hmsEntities.length && !paymentTerms.length) return;

    const buyerName = invoiceData._capture_buyer_entity_name;
    if (buyerName && !invoiceData.hms_entity && hmsEntities.length > 0) {
      const needle = buyerName.toLowerCase().replace(/\s+/g, '');
      const match = hmsEntities.find(e => {
        const label = (e.label || '').toLowerCase().replace(/\s+/g, '');
        return label === needle || label.includes(needle) || needle.includes(label);
      });
      if (match) {
        setInvoiceData(prev => ({ ...prev, hms_entity: match.value }));
      }
    }

    const ptText = invoiceData._capture_payment_terms;
    if (ptText && !invoiceData.payment_terms && paymentTerms.length > 0) {
      const needle = ptText.toLowerCase().replace(/\s+/g, '');
      
      // Strategy 1: exact match on term_code or description substring
      let match = paymentTerms.find(t => {
        const code = (t.term_code || '').toLowerCase().replace(/\s+/g, '');
        const desc = (t.description || '').toLowerCase().replace(/\s+/g, '');
        return code === needle || desc.includes(needle) || needle.includes(code);
      });

      // Strategy 2: extract days number from text and match against days_until_due
      if (!match) {
        const daysMatch = ptText.match(/(\d+)\s*(?:days?|d)\b/i);
        if (daysMatch) {
          const extractedDays = parseInt(daysMatch[1], 10);
          match = paymentTerms.find(t => t.days_until_due === extractedDays);
        }
      }

      // Strategy 3: check if term_code contains the same number found in the text
      if (!match) {
        const numbersInText = ptText.match(/\d+/g);
        if (numbersInText) {
          for (const num of numbersInText) {
            match = paymentTerms.find(t => {
              const code = (t.term_code || '').toUpperCase();
              return code.includes(num) && code.startsWith('NET');
            });
            if (match) break;
          }
        }
      }

      // Strategy 4: "immediate" or "on delivery" → match COD/IMMED (0 days)
      if (!match) {
        const lowerText = ptText.toLowerCase();
        if (lowerText.includes('immediate') || lowerText.includes('on delivery') || lowerText.includes('cod')) {
          match = paymentTerms.find(t => t.days_until_due === 0 && 
            ['IMMED', 'COD'].includes((t.term_code || '').toUpperCase()));
        }
      }

      // Strategy 5: fallback to CUSTOM — the due date was already extracted
      if (!match) {
        match = paymentTerms.find(t => (t.term_code || '').toUpperCase() === 'CUSTOM');
      }

      if (match) {
        setInvoiceData(prev => ({ ...prev, payment_terms: match.id }));
      }
    }
  }, [hmsEntities, paymentTerms, invoiceData._capture_buyer_entity_name, invoiceData._capture_payment_terms]);
  
  // Auto-select or auto-search supplier from capture extraction
  useEffect(() => {
    if (!invoiceData.partner) {
      if (capturePartner) {
        handleSelectPartner(capturePartner);
      } else if (captureSupplierName) {
        const autoSearchSupplier = async () => {
          try {
            const results = await crmService.searchProfilesByName(captureSupplierName);
            const approved = results.filter(p => p.approval_status === 'APPROVED' || p.approval_status === undefined);
            if (approved.length > 0) {
              const match = approved[0];
              handleSelectPartner({
                id: match.id,
                legal_entity_name: match.legal_entity_name,
                trading_short_name: match.trading_short_name,
              });
            } else {
              setPartnerSearchTerm(captureSupplierName);
            }
          } catch (err) {
            console.error('Auto supplier search failed:', err);
            setPartnerSearchTerm(captureSupplierName);
          }
        };
        autoSearchSupplier();
      }
    }
  }, [capturePartner, captureSupplierName]);
  
  const loadIssuerInfo = async () => {
    try {
      // Try from context first
      if (userInfo.first_name && userInfo.last_name) {
        setSessionUserName(`${userInfo.first_name} ${userInfo.last_name}`.trim());
      } else {
        // Fetch from API if context is empty (page reload)
        const userInfoData = await dashboardService.getUserInfo();
        updateUserInfo(userInfoData);
        setSessionUserName(`${userInfoData.first_name} ${userInfoData.last_name}`.trim());
      }
    } catch (error) {
      console.error('Error loading issuer info:', error);
      setSessionUserName('Automatic (person creating the invoice)');
    }
  };

  // The Issuer names whoever RAISED this document, which is not whoever is
  // looking at it -- only an unsaved document belongs to the person typing.
  const issuerFullName = resolveIssuerName({
    isPersisted,
    storedIssuerName: issuerName,
    sessionUserName,
  });
  
  useEffect(() => {
    // Initialize partner search term from saved data
    if (invoiceData.partner_name && !partnerSearchTerm) {
      setPartnerSearchTerm(invoiceData.partner_name);
    }
  }, [invoiceData.partner_name]);
  
  // Debounce partner search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPartnerSearch(partnerSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [partnerSearchTerm]);
  
  // Fetch partners when debounced search changes
  useEffect(() => {
    if (debouncedPartnerSearch.trim().length >= 2) {
      fetchApprovedPartners(debouncedPartnerSearch);
    } else {
      setFilteredPartners([]);
    }
  }, [debouncedPartnerSearch]);
  
  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (partnerSearchRef.current && !partnerSearchRef.current.contains(event.target)) {
        setShowPartnerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadDropdownData = async () => {
    try {
      setLoading(true);
      
      // Fetch all required dropdown data
      const [entitiesData, dropdownsData] = await Promise.all([
        fetchHMSEntities(),
        invoicingService.fetchInvoicingDropdowns()
      ]);
      
      setHmsEntities(entitiesData);
      const terms = dropdownsData.payment_terms || [];
      setPaymentTerms(terms);

      // If creating from delivery, pre-fill data
      if (sourceDelivery) {
        prefillFromDelivery(sourceDelivery);
      }
      
      // If creating from proforma, pre-fill data
      if (sourceProforma) {
        prefillFromProforma(sourceProforma);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      setLoading(false);
    }
  };
  
  const prefillFromDelivery = (delivery) => {
    setInvoiceData(prev => ({
      ...prev,
      hms_entity: delivery.hms_entity,
      partner_name: delivery.delivery_supplier_name || delivery.delivery_buyer_crm_partner_name,
      delivery_date: delivery.delivery_date,
      internal_reference: `Delivery: ${delivery.delivery_unique_number}\n` +
                         (delivery.seller_contract_number ? `Contract: ${delivery.seller_contract_number}` : ''),
      currency: delivery.sale_price_per_ton_contract_currency || 'USD',
    }));
    
    if (delivery.delivery_supplier_name) {
      setPartnerSearchTerm(delivery.delivery_supplier_name);
    }
  };
  
  const prefillFromProforma = (proforma) => {
    setInvoiceData(prev => ({
      ...prev,
      ...proforma,
      internal_reference: (proforma.internal_reference || '') + `\nFrom Pro-forma: ${proforma.invoice_number}`,
    }));
    
    // Pre-fill partner search term if partner already selected
    if (proforma.partner_name) {
      setPartnerSearchTerm(proforma.partner_name);
    }
  };
  
  const fetchApprovedPartners = async (searchTerm) => {
    try {
      setPartnerSearchLoading(true);
      
      // Use the SAME method as Outgoing Invoice screen
      const results = await crmService.searchProfilesByName(searchTerm);
      
      // Filter for APPROVED profiles only (client-side since API doesn't support it)
      const approvedProfiles = results.filter(profile => 
        profile.approval_status === 'APPROVED' || profile.approval_status === undefined
      );
      
      // Map to the format expected by the dropdown
      const partnersData = approvedProfiles.map(profile => ({
        id: profile.id,
        legal_entity_name: profile.legal_entity_name,
        trading_short_name: profile.trading_short_name,
        base_currency: profile.base_currency || 'USD',
        head_office_address: profile.head_office_address || '',
        display_name: profile.trading_short_name
          ? `${profile.legal_entity_name} (${profile.trading_short_name})`
          : profile.legal_entity_name
      }));
      
      setFilteredPartners(partnersData);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setFilteredPartners([]);
    } finally {
      setPartnerSearchLoading(false);
    }
  };

  const handleSelectPartner = async (partner) => {
    setInvoiceData(prev => ({
      ...prev,
      partner: partner.id,
      partner_name: partner.legal_entity_name,
      currency: partner.base_currency || prev.currency || 'USD'
    }));
    setPartnerSearchTerm(partner.legal_entity_name);
    setSelectedPartnerAddress(partner.head_office_address || '');
    setShowPartnerDropdown(false);

    // Fetch creditor number from partner
    try {
      const partnerDetails = await crmService.getProfile(partner.id);
      if (partnerDetails.creditor_number) {
        setInvoiceData(prev => ({ 
          ...prev, 
          creditor_number: partnerDetails.creditor_number 
        }));
      }
    } catch (error) {
      console.error('Error fetching partner details:', error);
    }
  };
  
  const handleNext = () => {
    // No validation on navigation - users can freely move between screens
    // Validation only happens on Save/Submit in Screen 3
    onNext();
  };
  
  if (loading) {
    return (
      <div className="invoice-loading">
        <div className="loading-spinner"></div>
        <p>Loading invoice data...</p>
      </div>
    );
  }
  
  return (
    <>
      <InvoiceValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        title="Complete Required Fields"
        errors={validationErrors}
        fieldIds={validationFieldIds}
      />
      
      <div className="invoice-screen-content">
        <div className="invoice-form-section">
          <h2 className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <FontAwesomeIcon icon={faBuilding} /> Invoice Header & Supplier Information
            </span>
            <span style={{ fontSize: '0.85em', color: '#fff', fontWeight: 'normal' }}>
              {invoiceData.hms_expense_number ? `Expense #: ${invoiceData.hms_expense_number}` : '(expense number will be generated on save)'}
            </span>
          </h2>
          
          <div className="invoice-form-grid">
            {/* Left Column */}
            <div className="invoice-form-column">
              {/* IMT Entity */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faBuilding} /> Entity
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {hmsEntities.find(e => e.value === invoiceData.hms_entity)?.label || 'N/A'}
                  </div>
                ) : (
                  <select
                    id="incoming_hms_entity_select"
                    className="form-select"
                    value={invoiceData.hms_entity || ''}
                    onChange={(e) => {
                      setInvoiceData(prev => ({ ...prev, hms_entity: parseInt(e.target.value) }));
                      clearFieldError('incoming_hms_entity_select');
                    }}
                    disabled={sourceDelivery || sourceProforma}
                  >
                    <option value="">Select Entity...</option>
                    {hmsEntities.map(entity => (
                      <option key={entity.value} value={entity.value}>
                        {entity.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Issuer */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faUser} /> Issuer
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={issuerFullName}
                  readOnly
                  disabled
                />
              </div>
              
              {/* Supplier Invoice Number */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faFileInvoice} /> Supplier Invoice Number
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {invoiceData.supplier_invoice_number || 'N/A'}
                  </div>
                ) : (
                  <input
                    type="text"
                    id="supplier_invoice_number"
                    className="form-input"
                    value={invoiceData.supplier_invoice_number || ''}
                    onChange={(e) => {
                      setInvoiceData(prev => ({ ...prev, supplier_invoice_number: e.target.value }));
                      clearFieldError('supplier_invoice_number');
                    }}
                    placeholder="Enter supplier's invoice number..."
                  />
                )}
              </div>
              
              {/* IMT Expense Number - Only show if it exists (after save) */}
              {invoiceData.hms_expense_number && (
                <div className="form-field">
                  <label className="field-label">
                    <FontAwesomeIcon icon={faFileInvoice} /> IMT Expense Number
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={invoiceData.hms_expense_number}
                    readOnly
                    disabled
                  />
                </div>
              )}
              
              {/* Invoice Date */}
              <div className="form-field">
                <InvoiceDatePicker
                  value={invoiceData.invoice_date || ''}
                  onChange={(e) => {
                    setInvoiceData(prev => ({ ...prev, invoice_date: e.target.value }));
                    clearFieldError('invoice_date');
                  }}
                  name="invoice_date"
                  id="invoice_date"
                  label={
                    <span>
                      <FontAwesomeIcon icon={faCalendarAlt} /> Invoice Date
                    </span>
                  }
                  placeholder="Select invoice date"
                  required={true}
                  className="form-input"
                  readOnly={isReadOnly}
                />
              </div>
              
              {/* Internal Reference - 3 separate fields */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Internal Reference
                </label>
                <div className="internal-reference-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="delivery number"
                    value={parseInternalReference(invoiceData.internal_reference).delivery}
                    onChange={(e) => updateInternalReference('delivery', e.target.value)}
                    readOnly={isReadOnly}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="contracts number"
                    value={parseInternalReference(invoiceData.internal_reference).contracts}
                    onChange={(e) => updateInternalReference('contracts', e.target.value)}
                    readOnly={isReadOnly}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="purchase recap numbers"
                    value={parseInternalReference(invoiceData.internal_reference).purchaseRecaps}
                    onChange={(e) => updateInternalReference('purchaseRecaps', e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="invoice-form-column">
              {/* Partner Name - Searchable CRM Profile Dropdown */}
              <div className="form-field" ref={partnerSearchRef}>
                <label className="field-label required">
                  <FontAwesomeIcon icon={faHandshake} /> Supplier (CRM Profile)
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {invoiceData.partner_name || 'N/A'}
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'relative' }}>
                      <FontAwesomeIcon 
                        icon={faSearch} 
                        style={{ 
                          position: 'absolute', 
                          left: '12px', 
                          top: '50%', 
                          transform: 'translateY(-50%)', 
                          color: '#666',
                          pointerEvents: 'none'
                        }} 
                      />
                      <input
                        type="text"
                        id="incoming_partner_search_input"
                        className="form-input"
                        style={{ paddingLeft: '36px' }}
                        value={partnerSearchTerm}
                        onChange={(e) => {
                          setPartnerSearchTerm(e.target.value);
                          setSelectedPartnerAddress('');
                          setShowPartnerDropdown(true);
                          clearFieldError('incoming_partner_search_input');
                        }}
                        onFocus={() => setShowPartnerDropdown(true)}
                        placeholder="Search APPROVED CRM profiles by Legal Entity Name..."
                        disabled={sourceDelivery || sourceProforma}
                      />
                    </div>
                    
                    {showPartnerDropdown && debouncedPartnerSearch.trim().length >= 2 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}>
                        {partnerSearchLoading ? (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                            Searching...
                          </div>
                        ) : filteredPartners.length > 0 ? (
                          filteredPartners.map(partner => (
                            <div
                              key={partner.id}
                              onClick={() => handleSelectPartner(partner)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              <div style={{ fontWeight: '600', color: '#4a6b25' }}>
                                {partner.legal_entity_name}
                              </div>
                              {partner.trading_short_name && (
                                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                                  Trading as: {partner.trading_short_name}
                                </div>
                              )}
                              {partner.head_office_address && (
                                <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <FontAwesomeIcon icon={faMapMarkerAlt} /> {partner.head_office_address}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                            No APPROVED partners found for "{debouncedPartnerSearch}"
                          </div>
                        )}
                      </div>
                    )}
                    {invoiceData.partner && selectedPartnerAddress && (
                      <div style={{ fontSize: '0.8em', color: '#888', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> {selectedPartnerAddress}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Creditor Number */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faIdCard} /> Creditor Nr
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={invoiceData.creditor_number || '(from CRM)'}
                  readOnly
                  disabled
                />
              </div>
              
              {/* Delivery Date */}
              <div className="form-field">
                <InvoiceDatePicker
                  value={invoiceData.delivery_date || ''}
                  onChange={(e) => {
                    setInvoiceData(prev => ({ ...prev, delivery_date: e.target.value }));
                    clearFieldError('delivery_date');
                  }}
                  name="delivery_date"
                  id="delivery_date"
                  label={
                    <span>
                      <FontAwesomeIcon icon={faTruck} /> Delivery Date
                    </span>
                  }
                  placeholder="Select delivery date"
                  className="form-input"
                  readOnly={isReadOnly}
                />
              </div>
              
              {/* Due Date */}
              <div className="form-field">
                <InvoiceDatePicker
                  value={invoiceData.due_date || ''}
                  onChange={(e) => {
                    setInvoiceData(prev => ({ ...prev, due_date: e.target.value }));
                    clearFieldError('due_date');
                  }}
                  name="due_date"
                  id="due_date"
                  label={
                    <span>
                      <FontAwesomeIcon icon={faCalendarAlt} /> Due Date
                    </span>
                  }
                  placeholder="Enter due date from invoice"
                  required={true}
                  className="form-input"
                  readOnly={isReadOnly}
                />
              </div>
              
              {/* Payment Terms */}
              <div className="form-field">
                <label className="field-label required">
                  <FontAwesomeIcon icon={faMoneyBillWave} /> Payment Terms
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {paymentTerms.find(t => t.id === invoiceData.payment_terms)?.term_code || 'N/A'}
                  </div>
                ) : (
                  <select
                    id="incoming_payment_terms_select"
                    className="form-select"
                    value={invoiceData.payment_terms || ''}
                    onChange={(e) => {
                      setInvoiceData(prev => ({ ...prev, payment_terms: parseInt(e.target.value) }));
                      clearFieldError('incoming_payment_terms_select');
                    }}
                  >
                    <option value="">Select Payment Terms...</option>
                    {paymentTerms.map(term => (
                      <option key={term.id} value={term.id}>
                        {term.term_code} - {term.description}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* Supply Reference */}
              <div className="form-field">
                <label className="field-label">
                  <FontAwesomeIcon icon={faFileAlt} /> Supply Reference
                </label>
                {isReadOnly ? (
                  <div className="form-display-value">
                    {invoiceData.supply_reference || 'N/A'}
                  </div>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    value={invoiceData.supply_reference || ''}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, supply_reference: e.target.value }))}
                    placeholder="Enter supply reference..."
                  />
                )}
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="screen-navigation" style={{ justifyContent: 'flex-end' }}>
            <button className="btn-primary" type="button" onClick={handleNext}>
              Next: Line Items <FontAwesomeIcon icon={faBox} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncomingInvoiceScreen1;
